import Close from '@mui/icons-material/Close'
import { Box, IconButton } from '@mui/material'
import Grow from '@mui/material/Grow'
import queryString from 'query-string'
import { memo, useRef, useCallback, useState } from 'react'
import { makeStyles } from 'tss-react/mui'

const customBreakpoints = {
  xs: 350,
  sm: 425,
  md: 1152,
  lg: 1250,
  xl: 1452,
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  withHref: {
    cursor: 'pointer',
  },
  imageContainer: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
  textContainer: {
    'padding': theme.spacing(8),
    'flexGrow': 1,
    ...theme.typography.h5,
    [theme.breakpoints.up('md')]: {
      ...theme.typography.h4,
    },
    '& em': {
      color: theme.palette.secondary.main,
      fontStyle: 'normal',
    },
  },
  logo: {
    maxWidth: 200,
    maxHeight: 100,
    objectFit: 'contain',
    verticalAlign: 'middle',
    marginTop: '1em',
  },
  image: {
    maxWidth: 450,
    maxHeight: 400,
    verticalAlign: 'middle',
  },
  messageWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '400px',
    maxHeight: '400px',
    width: '100%',
  },
  fixedLogo: {
    maxWidth: 200,
    marginTop: theme.spacing(4),
    marginLeft: theme.spacing(4),
  },
  fixedSize: {
    height: '400px',
    maxHeight: '400px',
    minHeight: '400px',
  },
  fixedImageContainer: {
    display: 'flex',
    [theme.breakpoints.down(customBreakpoints.md)]: {
      display: 'none',
    },
    justifyContent: 'center',
    alignItems: 'center',
    height: '400px',
    width: '450px',
    maxHeight: '400px',
    minHeight: '400px',
    [theme.breakpoints.down(customBreakpoints.lg)]: {
      height: '300px',
      width: '350px',
      maxHeight: '300px',
      minHeight: '300px',
    },
  },
  fixedTextContainer: {
    'margin': theme.spacing(4),
    'flexGrow': 1,
    ...theme.typography.h5,
    'fontSize': '30px',
    [theme.breakpoints.between(0, customBreakpoints.xs)]: {
      fontSize: '20px',
    },
    [theme.breakpoints.between(customBreakpoints.xs + 1, customBreakpoints.sm)]:
      {
        fontSize: '22px',
      },
    [theme.breakpoints.between(customBreakpoints.sm + 1, customBreakpoints.xl)]:
      {
        fontSize: '25px',
      },
    '& em': {
      color: theme.palette.secondary.main,
      fontStyle: 'normal',
    },
  },
  fixedImage: {
    maxWidth: 450,
    [theme.breakpoints.down(customBreakpoints.lg)]: {
      maxWidth: '350px',
    },
  },
  hideIcon: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
}))

/** @type {React.FC<{ logo: string, image?: string, text: React.ReactNode, href?: string, name?: string, onClick?: () => void, onHide?: () => void, fixedSize?: boolean }>} */
const BaseBillboard = (props) => {
  const { fixedSize } = props
  const { classes, cx } = useStyles()
  const rootClassName = cx(classes.root, {
    [classes.withHref]: !!props.href,
    [classes.fixedSize]: fixedSize,
  })

  const [isHidden, setIsHidden] = useState(false)

  // We need to do click rejection, otherwise href + swiping the carousel ===
  // open the link.
  const didMoveRef = useRef(false)
  const isMouseDownRef = useRef(false)
  const onMouseDown = useCallback(() => {
    didMoveRef.current = false
    isMouseDownRef.current = true
  }, [])
  const onMouseMove = useCallback(() => {
    if (!isMouseDownRef.current) {
      return
    }

    didMoveRef.current = true
  }, [])
  const onMouseUp = useCallback(() => {
    if (props.onClick) {
      props.onClick()
    }

    if (!props.href || !isMouseDownRef.current || didMoveRef.current) {
      return
    }

    let finalUrl
    // R1 completely breaks when reading query params, so just disable all UTM
    // params for our stuff.
    if (
      props.href.includes('promoboxx.com') ||
      props.href.includes('pbxx.io')
    ) {
      finalUrl = props.href
    } else {
      const params = queryString.stringify({
        utm_source: 'pbxx-retailer',
        utm_medium: 'billboard',
        utm_campaign: props.name,
      })

      const joiner = props.href.includes('?') ? '&' : '?'

      finalUrl = `${props.href}${joiner}${params}`
    }

    window.open(finalUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //Alternative way to render Billboards with a fixed size min/max
  if (fixedSize) {
    return (
      <Grow
        appear={false}
        enter={false}
        in={!isHidden}
        // dispatch `onHide` after the animation has finished.
        onExited={() => {
          props.onHide && props.onHide()
        }}
      >
        <div
          className={rootClassName}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
        >
          {props.onHide ? (
            <IconButton
              size="small"
              className={classes.hideIcon}
              onClick={() => setIsHidden(true)}
              // We need to stop propagation for these events, otherwise it's picked
              // up by the href handling.
              onMouseDown={(event) => event.stopPropagation()}
              onMouseUp={(event) => event.stopPropagation()}
            >
              <Close fontSize="inherit" />
            </IconButton>
          ) : undefined}
          <div className={classes.messageWrapper}>
            <img alt={''} className={classes.fixedLogo} src={props.logo} />
            <Box className={classes.fixedTextContainer}>
              <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: props.text,
                }}
              />
            </Box>
          </div>
          {props.image && (
            <div className={classes.fixedImageContainer}>
              <img alt={''} className={classes.fixedImage} src={props.image} />
            </div>
          )}
        </div>
      </Grow>
    )
  } else {
    // Default rendering style for Billboards, no fixed height...
    return (
      <Grow
        appear={false}
        enter={false}
        in={!isHidden}
        // dispatch `onHide` after the animation has finished.
        onExited={() => {
          props.onHide && props.onHide()
        }}
      >
        <div
          className={rootClassName}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
        >
          {props.onHide ? (
            <IconButton
              size="small"
              className={classes.hideIcon}
              onClick={() => setIsHidden(true)}
              // We need to stop propagation for these events, otherwise it's picked
              // up by the href handling.
              onMouseDown={(event) => event.stopPropagation()}
              onMouseUp={(event) => event.stopPropagation()}
            >
              <Close fontSize="inherit" />
            </IconButton>
          ) : undefined}
          <div className={classes.textContainer}>
            <img alt={''} className={classes.logo} src={props.logo} />
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: props.text,
              }}
            />
          </div>
          {props.image && (
            <div className={classes.imageContainer}>
              <img alt={''} className={classes.image} src={props.image} />
            </div>
          )}
        </div>
      </Grow>
    )
  }
}

export default memo(BaseBillboard)
